// packages/web/src/router.tsx
import { createRootRoute, createRoute, createRouter, redirect, Outlet } from '@tanstack/react-router'
import { z } from 'zod'
import { StorefrontPage } from './App'
import { ProductPage } from './pages/ProductPage'
import { CheckoutPage } from './pages/CheckoutPage'
import { TrackOrderPage } from './pages/TrackOrderPage'
import { QualityGuidePage } from './pages/QualityGuidePage'
import { CustomerLoginPage } from './pages/CustomerLoginPage'
import { CustomerRegisterPage } from './pages/CustomerRegisterPage'
import { CustomerOrdersPage } from './pages/CustomerOrdersPage'
import { AdminLoginPage } from './components/AdminLoginPage'
import { AdminLayout } from './components/AdminLayout'
import { AdminOverview } from './components/AdminOverview'
import { AdminProducts } from './components/AdminProducts'
import { AdminCategories } from './components/AdminCategories'
import { AdminCombos } from './components/AdminCombos'
import { AdminOrders } from './components/AdminOrders'
import { AdminStock } from './components/AdminStock'
import { AdminSettings } from './components/AdminSettings'
import { AdminQuotes } from './components/AdminQuotes'
import { AdminInvoices } from './components/AdminInvoices'
import { AdminOrderDetailPage } from './pages/admin/AdminOrderDetailPage'
import { AdminOrderStatusPage } from './pages/admin/AdminOrderStatusPage'
import { AdminQuoteCreatePage } from './pages/admin/AdminQuoteCreatePage'
import { AdminStockAdjustPage } from './pages/admin/AdminStockAdjustPage'
import { AdminInvoiceMarkPaidPage } from './pages/admin/AdminInvoiceMarkPaidPage'
import { AdminInvoiceVoidPage } from './pages/admin/AdminInvoiceVoidPage'
import { AUTH_TOKEN_KEY, AUTH_USER_KEY } from './lib/authStorage'

function isAdminStored(): boolean {
  const token = localStorage.getItem(AUTH_TOKEN_KEY)
  const user = localStorage.getItem(AUTH_USER_KEY)
  if (!token || !user) return false
  try {
    const u = JSON.parse(user)
    return u?.role === 'admin' || u?.role === 'superadmin'
  } catch {
    return false
  }
}

function isCustomerStored(): boolean {
  const token = localStorage.getItem(AUTH_TOKEN_KEY)
  const user = localStorage.getItem(AUTH_USER_KEY)
  if (!token || !user) return false
  try {
    const u = JSON.parse(user)
    return u?.role === 'customer'
  } catch {
    return false
  }
}

const rootRoute = createRootRoute({ component: () => <Outlet /> })

const storefrontRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: StorefrontPage,
})

const productRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/product/$productId',
  component: ProductPage,
})

const checkoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/checkout',
  beforeLoad: ({ location }) => {
    if (!isCustomerStored()) {
      throw redirect({
        to: '/account/login',
        search: { redirect: location.pathname },
      })
    }
  },
  component: CheckoutPage,
})

const trackSearchSchema = z.object({
  id: z.string().optional(),
})

const trackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/track',
  validateSearch: trackSearchSchema,
  component: TrackOrderPage,
})

const qualityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/quality',
  component: QualityGuidePage,
})

const accountLoginSearchSchema = z.object({
  redirect: z.string().optional(),
})

const accountLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account/login',
  validateSearch: accountLoginSearchSchema,
  component: CustomerLoginPage,
})

const accountRegisterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account/register',
  validateSearch: accountLoginSearchSchema,
  component: CustomerRegisterPage,
})

const accountOrdersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account/orders',
  beforeLoad: ({ location }) => {
    if (!isCustomerStored()) {
      throw redirect({
        to: '/account/login',
        search: { redirect: location.pathname },
      })
    }
  },
  component: CustomerOrdersPage,
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: AdminLoginPage,
})

const adminLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  beforeLoad: ({ location }) => {
    if (!isAdminStored()) {
      throw redirect({
        to: '/login',
        search: { redirect: location.pathname + location.searchStr },
      })
    }
  },
  component: AdminLayout,
})

const adminIndexRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/admin/overview', replace: true })
  },
})

const adminOverviewRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/overview',
  component: AdminOverview,
})

const adminProductsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/products',
  component: AdminProducts,
})

const adminCategoriesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/categories',
  component: AdminCategories,
})

const adminCombosRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/combos',
  component: AdminCombos,
})

const adminOrdersRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/orders',
  component: AdminOrders,
})

const adminOrderDetailRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/orders/$orderId',
  component: AdminOrderDetailPage,
})

const adminOrderStatusRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/orders/$orderId/status',
  component: AdminOrderStatusPage,
})

const adminStockRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/stock',
  component: AdminStock,
})

const stockAdjustSearchSchema = z.object({
  change: z.coerce.number().optional(),
})

const adminStockAdjustRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/stock/$variantId/adjust',
  validateSearch: stockAdjustSearchSchema,
  component: AdminStockAdjustPage,
})

// Hidden for this phase — routes kept for next phase, not shown in admin nav
const adminQuotesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/quotes',
  component: AdminQuotes,
})

const adminQuoteCreateRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/quotes/new',
  component: AdminQuoteCreatePage,
})

const adminInvoicesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/invoices',
  component: AdminInvoices,
})

const adminInvoiceMarkPaidRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/invoices/$invoiceId/mark-paid',
  component: AdminInvoiceMarkPaidPage,
})

const adminInvoiceVoidRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/invoices/$invoiceId/void',
  component: AdminInvoiceVoidPage,
})

const adminSettingsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/settings',
  component: AdminSettings,
})

const routeTree = rootRoute.addChildren([
  storefrontRoute,
  productRoute,
  checkoutRoute,
  trackRoute,
  qualityRoute,
  accountLoginRoute,
  accountRegisterRoute,
  accountOrdersRoute,
  loginRoute,
  adminLayoutRoute.addChildren([
    adminIndexRoute,
    adminOverviewRoute,
    adminProductsRoute,
    adminCategoriesRoute,
    adminOrdersRoute,
    adminOrderDetailRoute,
    adminOrderStatusRoute,
    adminStockRoute,
    adminStockAdjustRoute,
    adminQuotesRoute,
    adminQuoteCreateRoute,
    adminInvoicesRoute,
    adminInvoiceMarkPaidRoute,
    adminInvoiceVoidRoute,
    adminSettingsRoute,
  ]),
])

export const router = createRouter({ routeTree, defaultPreload: false })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
